import { GLOBAL_TAXONOMY_TERRITORY_TYPES } from 'modules/global-topic/common/global-topic.consts'

import { INDUSTRY_TOPIC_PREFIX, NICHE_PREFIX } from './niche.consts'
import type { INicheSelectionSource, IPromotedNicheTerritory } from './types'

/**
 * Pure detection helpers for the Promoted Niche `promotionsV2` field. Kept free of
 * store/URL dependencies (unlike `niche.utils`) so they can be imported from
 * lightweight utils without dragging side-effectful modules into their tests.
 */

/** A territory is an Industry Category when its id carries the `industry_topic.` prefix. */
export const isIndustryCategoryTerritory = (territoryId: string) => {
  return territoryId.startsWith(INDUSTRY_TOPIC_PREFIX)
}

export const isGlobalTaxonomyTerritoryId = (territoryId: string) => {
  const [type] = territoryId.split('.')
  return GLOBAL_TAXONOMY_TERRITORY_TYPES.includes(type)
}

/**
 * The canonical full territory id a niche-card item represents when added to a
 * selection (add-to checkbox, create-report). Single source of truth shared by
 * both flows so they never disagree:
 *
 * - A promoted niche represents the Category it was promoted into
 *   (`promotedTargetId`, already an `industry_topic.{uuid}`).
 * - An item whose own territory is already a Category or a global taxonomy type represents itself.
 * - Everything else is a plain niche, normalised to `niche.{uuid}`.
 */
export const resolveNicheSelectionTerritoryId = (item: INicheSelectionSource): string => {
  if (item.isPromoted && item.promotedTargetId) return item.promotedTargetId
  if (item.territoryId && isIndustryCategoryTerritory(item.territoryId)) return item.territoryId
  if (item.territoryId && isGlobalTaxonomyTerritoryId(item.territoryId)) return item.territoryId

  const rawId = item.id ?? item.territoryId ?? ''
  const bareId = rawId.includes('.') ? rawId.split('.')[1] : rawId
  return `${NICHE_PREFIX}${bareId}`
}

/**
 * A territory is a promoted industry when it appears as the `target` of one of
 * its own promotions (detection rule from the backend API doc). Used on the
 * Category page to branch read-only / context-source behaviour.
 */
export const isPromotedIndustry = (territory?: IPromotedNicheTerritory | null): boolean =>
  Boolean(
    territory?.promotionsV2?.some(
      promotion => Boolean(promotion?.target?.territoryId) && promotion?.target?.territoryId === territory.territoryId
    )
  )

/**
 * When a niche has been promoted, it is the `source` of a promotion and the
 * resulting industry is that promotion's `target`. Returns the target's
 * `territoryId` so the niche card can link to the new industry page, or
 * `undefined` when the niche is not promoted.
 */
export const resolvePromotedNicheTargetId = (niche?: IPromotedNicheTerritory | null): string | undefined =>
  resolvePromotedNichePromotion(niche)?.target?.territoryId ?? undefined

/**
 * The promotion where this niche is the `source` — i.e. the promotion that turned
 * it into an industry. Shared by the id / name resolvers below so they always read
 * from the same entry.
 */
const resolvePromotedNichePromotion = (niche?: IPromotedNicheTerritory | null) =>
  niche?.promotionsV2?.find(
    promotion => Boolean(promotion?.source?.territoryId) && promotion?.source?.territoryId === niche.territoryId
  )

/**
 * The display name of the Category a niche was promoted into (the promotion
 * `target`). `getTerritory(niche.<id>)` keeps returning the niche's own `name`
 * even after the backend swaps its metrics to the Category, so consumers must
 * read the Category name from the promotion to show the promoted label (which
 * carries the parent country, e.g. `"PlayStation Games - US"`). `undefined` when
 * the niche is not promoted.
 */
export const resolvePromotedNicheTargetName = (niche?: IPromotedNicheTerritory | null): string | undefined =>
  resolvePromotedNichePromotion(niche)?.target?.name ?? undefined

/**
 * Resolves the full `industry_topic.{uuid}` territory id of the promoted scope
 * the current niches belong to — the single source of truth for both
 * scoped child-niche links and copy-query parent override:
 *
 * - On the dedicated scoped niche route the scope comes from the URL
 *   (`scopeId`, a full `industry_topic.{uuid}` from `useNicheScope`).
 * - On a promoted industry's own Category page there is no scoped route, so the
 *   scope is the current territory itself (detected via its `promotionsV2`).
 * - Otherwise `undefined` — unscoped, today's behaviour.
 *
 * Consumers derive what they need: the bare uuid for child-niche links
 * (`/category/:uuid/niche/...`), and the full id as `parentTerritoryIds` for
 * copy-query (`formatParentTerritoryIds` derives `topic_ids` by splitting on `.`).
 */
export const resolvePromotedScopeId = (
  scopeId: string | null | undefined,
  territoryId: string,
  territory?: IPromotedNicheTerritory | null
): string | undefined => {
  if (scopeId) return scopeId
  if (isPromotedIndustry(territory)) return territoryId
  return undefined
}

/**
 * Resolves the context entry that should be excluded from a promoted industry's
 * context list — the `sourceContextId` carried by the promotion where this
 * territory is the `target` — so the user does not see the context the niche was
 * originally promoted from.
 *
 * The exclusion is applied inside `useNicheContextManager` (via its
 * `excludeContextId` param) rather than downstream, so the manager never lists
 * or auto-selects it — keeping `contexts` and `activeContext` consistent.
 *
 * - promoted + a promotion carries `sourceContextId` → returns the id to exclude.
 * - `sourceContextId` null (e.g. legacy niche with no context) → `undefined`.
 * - not promoted → returns `undefined` (no exclusion).
 */
export const resolvePromotedNicheExcludedContextId = (
  territory?: IPromotedNicheTerritory | null
): string | undefined => {
  if (!isPromotedIndustry(territory)) {
    return undefined
  }

  return (
    territory?.promotionsV2?.find(
      promotion => promotion?.target?.territoryId === territory.territoryId && Boolean(promotion?.sourceContextId)
    )?.sourceContextId ?? undefined
  )
}
